import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  formName: string;
  setFormName: (name: string) => void;
  // Modal state
  isModalOpen: boolean;
  modalMessage: string;
  requestNavigation: (onConfirm: () => void) => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  resetUnsavedChanges: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formName, setFormName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const resetUnsavedChanges = useCallback(() => {
    setHasUnsavedChanges(false);
    setFormName('');
  }, []);

  const requestNavigation = useCallback((onConfirm: () => void) => {
    if (hasUnsavedChanges) {
      setPendingAction(() => onConfirm);
      setIsModalOpen(true);
    } else {
      onConfirm();
    }
  }, [hasUnsavedChanges]);

  const confirmNavigation = useCallback(() => {
    setIsModalOpen(false);
    resetUnsavedChanges();
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction, resetUnsavedChanges]);

  const cancelNavigation = useCallback(() => {
    setIsModalOpen(false);
    setPendingAction(null);
  }, []);

  const modalMessage = formName 
    ? `Tienes cambios sin guardar en "${formName}". ¿Estás seguro de que quieres salir? Se perderán los datos no guardados.`
    : 'Tienes cambios sin guardar. ¿Estás seguro de que quieres salir? Se perderán los datos no guardados.';

  return (
    <UnsavedChangesContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        formName,
        setFormName,
        isModalOpen,
        modalMessage,
        requestNavigation,
        confirmNavigation,
        cancelNavigation,
        resetUnsavedChanges,
      }}
    >
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges debe usarse dentro de UnsavedChangesProvider');
  }
  return context;
}

// Hook para usar en formularios
export function useTrackUnsavedChanges(isDirty: boolean, formName: string) {
  const { setHasUnsavedChanges, setFormName, resetUnsavedChanges } = useUnsavedChanges();

  useEffect(() => {
    if (isDirty) {
      setHasUnsavedChanges(true);
      setFormName(formName);
    } else {
      resetUnsavedChanges();
    }
  }, [isDirty, formName, setHasUnsavedChanges, setFormName, resetUnsavedChanges]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      resetUnsavedChanges();
    };
  }, [resetUnsavedChanges]);
}
